const CONTROL_LABELS: Record<string, string> = {
  manual: "Manual",
  cordless: "Cordless",
  motorized: "A-OK Matter motorized",
};

const SHADING_LABELS: Record<string, string> = {
  "semi-blackout": "Semi blackout",
  blackout: "Blackout",
  transparent: "Transparent",
};

export interface MeasurementItem {
  roomName?: string;
  label?: string;
  blindCode?: string;
  controlType?: string;
  opacity?: string;
  width?: number;
  height?: number;
  unit?: string;
  hasSolarPanel?: boolean;
  isRemote?: boolean;
}

export interface MeasurementOrder {
  orderId: string;
  orderNumber?: string;
  createdAt: string;
  customerName?: string;
  customerEmail?: string;
  shippingAddress?: {
    name?: string;
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
  };
  items: MeasurementItem[];
}

/** Build and download an A4 measurement sheet: customer, address, every room
 * with its windows/sizes, remotes, and the solar-panel count. */
export async function downloadMeasurementsPdf(
  order: MeasurementOrder,
): Promise<void> {
  // Lazy-loaded so jsPDF/autotable stay out of the initial bundle.
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const margin = 14;
  let y = 18;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(90, 62, 27);
  doc.text("Do Your Shades", margin, y);
  y += 6;

  doc.setFontSize(11);
  doc.setTextColor(30, 30, 30);
  doc.text(
    `Measurement sheet — ${order.orderNumber ?? order.orderId}`,
    margin,
    y,
  );
  y += 6;

  const placed = new Date(order.createdAt);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text(
    Number.isNaN(placed.getTime())
      ? ""
      : `Placed ${placed.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })}`,
    margin,
    y,
  );
  y += 8;

  const addr = order.shippingAddress;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(order.customerName || addr?.name || "Customer", margin, y);
  y += 5;

  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);
  const addrLines = [
    addr?.line1,
    addr?.line2,
    [addr?.city, addr?.state, addr?.zip].filter(Boolean).join(", "),
    addr?.phone,
  ].filter((l): l is string => !!l);
  for (const line of addrLines) {
    doc.text(line, margin, y);
    y += 5;
  }
  y += 4;

  const rooms: { room: string; items: MeasurementItem[] }[] = [];
  const byRoom = new Map<string, (typeof rooms)[number]>();
  for (const item of order.items) {
    const room = item.roomName?.trim() || "Room";
    let group = byRoom.get(room);
    if (!group) {
      group = { room, items: [] };
      byRoom.set(room, group);
      rooms.push(group);
    }
    group.items.push(item);
  }

  let solarCount = 0;
  for (const group of rooms) {
    const windows = group.items.filter((i) => !i.isRemote);
    const remotes = group.items.filter((i) => i.isRemote);
    const solarInRoom = windows.filter((w) => w.hasSolarPanel).length;
    solarCount += solarInRoom;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(group.room, margin, y);

    const body: string[][] = windows.map((w) => [
      w.label || "—",
      w.blindCode ?? "—",
      CONTROL_LABELS[w.controlType ?? ""] ?? w.controlType ?? "—",
      SHADING_LABELS[w.opacity ?? ""] ?? w.opacity ?? "—",
      w.width != null ? `${w.width} ${w.unit ?? "in"}` : "—",
      w.height != null ? `${w.height} ${w.unit ?? "in"}` : "—",
      w.hasSolarPanel ? "Yes" : "",
    ]);
    for (const r of remotes) {
      const channels = r.blindCode === "REMOTE-16CH" ? "16 channel" : "6 channel";
      body.push(["Remote control", r.blindCode ?? "—", channels, "", "", "", ""]);
    }
    if (solarInRoom > 0) {
      body.push(["Solar panel", "", "", "", "", "", String(solarInRoom)]);
    }

    if (body.length) {
      autoTable(doc, {
        startY: y + 2,
        margin: { left: margin, right: margin },
        head: [
          ["Window", "Style", "Control", "Shading", "Width", "Height", "Solar"],
        ],
        body,
        theme: "plain",
        styles: {
          fontSize: 9,
          cellPadding: 2.5,
          textColor: [0, 0, 0],
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fontStyle: "bold",
          fillColor: false,
          textColor: [0, 0, 0],
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
        },
        columnStyles: {
          4: { halign: "right" },
          5: { halign: "right" },
          6: { halign: "center" },
        },
      });
      y =
        (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable
          ?.finalY ?? y;
    }
    y += 6;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`Solar panels: ${solarCount}`, margin, y);

  doc.save(`${order.orderNumber ?? order.orderId}-measurements.pdf`);
}
